import React, { useState } from "react";
import { Row, Col } from "react-bootstrap";
import { CommonModal, CommonSelect, CommonInput, CommonButton } from "../Input";
import countriesAll from "../../../data/countries-all";

const SectionContainer = ({ title, children }) => (
  <div className="mb-4">
    <h6 className="fw-bold mb-3 fs-18 text-black">{title}</h6>
    <div className="bg-white p-3 rounded-2 border border-light">
      <Row className="g-3">{children}</Row>
    </div>
  </div>
);

const INITIAL_STATE = {
  country: null,
  currency: null,
  receiverType: null,
  receivingMethod: null,
  relationship: null,
  // ID info
  idType: null,
  idNumber: "",
  idExpiryDate: null,
  dob: null,
  // Personal info
  firstName: "",
  middleName: "",
  lastName: "",
  middleInitial: "",
  jointAccountHolder: "",
  taxId: "",
  companyReg: "",
  nativeName: "",
  // Contact
  phone: "",
  phonePrefix: "+65",
  // Address
  address: "",
  city: "",
  state: "",
  postalCode: "",
  suburb: "",
  // Account details
  bank: null,
  iban: "",
  accountNumber: "",
  accountType: null,
  bankAccountTitle: "",
  transitNumber: "",
  sortCode: "",
  routingNumber: "",
  bsbNumber: "",
  branchName: "",
  branchCode: "",
  branchNumber: "",
  ifscNumber: "",
  swiftCode: "",
  institutionNumber: "",
};

const CreateBeneficiaryModal = ({ show, onHide, onSave, initialData }) => {
  const [formData, setFormData] = React.useState(INITIAL_STATE);

  React.useEffect(() => {
    if (show) {
      if (initialData) {
        setFormData({ ...INITIAL_STATE, ...initialData });
      } else {
        setFormData(INITIAL_STATE);
      }
    }
  }, [show, initialData]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isBankTransfer = formData.receivingMethod?.value === "Bank transfer";
  const isIndividual = formData.receiverType?.value === "Individual";

  const selectedCountryData = countriesAll?.find(
    (c) => c.name === formData.country?.value,
  );

  const currencyOptions = React.useMemo(() => {
    if (!selectedCountryData) return [];
    const local = selectedCountryData.localCurrencies || [];
    return local.map((curr) => ({
      value: curr.symbol,
      label: `${curr.name} (${curr.symbol})`,
    }));
  }, [selectedCountryData]);

  const isSaveDisabled =
    !formData.country ||
    !formData.currency ||
    !formData.receiverType ||
    !formData.receivingMethod;

  return (
    <CommonModal
      show={show}
      onHide={onHide}
      size="xl"
      fullscreen={true}
      title={
        <div>
          <h4 className="fw-bold text-black mb-1">
            {initialData ? "Edit beneficiary" : "Create beneficiary"}
          </h4>
          <p className="fs-13 text-muted mb-0 fw-normal">
            {initialData
              ? "Update details for this beneficiary"
              : "Create a new beneficiary for this customer"}
          </p>
        </div>
      }
      bodyClassName="bg-light"
      footer={
        <div className="d-flex justify-content-end gap-2 w-100 pb-3 pe-3">
          <CommonButton
            variant="outline"
            className="px-5 py-2"
            onClick={onHide}
            style={{ minWidth: "120px" }}
          >
            Cancel
          </CommonButton>
          <CommonButton
            variant="primary"
            className="px-5 py-2 text-white border-0"
            onClick={() => onSave(formData)}
            disabled={isSaveDisabled}
            style={{
              minWidth: "120px",
              opacity: isSaveDisabled ? 0.6 : 1,
              cursor: isSaveDisabled ? "not-allowed" : "pointer",
            }}
          >
            Save
          </CommonButton>
        </div>
      }
    >
      <div className="px-1 py-1">
        <SectionContainer title="Basic information">
          <Col md={true}>
            <CommonSelect
              label="Country"
              options={
                countriesAll?.map((country) => ({
                  value: country.name,
                  label: country.name,
                })) || [{ value: "Singapore", label: "Singapore" }]
              }
              disabled={initialData}
              value={formData.country}
              onChange={(val) => {
                handleChange("country", val);
                // Reset currency when country changes
                handleChange("currency", null);
              }}
            />
          </Col>
          <Col md={true}>
            <CommonSelect
              label="Currency"
              options={currencyOptions}
              disabled={initialData}
              value={formData.currency}
              onChange={(val) => handleChange("currency", val)}
            />
          </Col>
          <Col md={true}>
            <CommonSelect
              label="Receiver type"
              options={[
                { value: "Individual", label: "Individual" },
                { value: "Organization", label: "Organization" },
              ]}
              disabled={initialData}
              value={formData.receiverType}
              onChange={(val) => handleChange("receiverType", val)}
            />
          </Col>
          <Col md={true}>
            <CommonSelect
              label="Receiving method"
              options={[
                { value: "Bank transfer", label: "Bank transfer" },
                { value: "Cash pickup", label: "Cash pickup" },
              ]}
              disabled={initialData}
              value={formData.receivingMethod}
              onChange={(val) => handleChange("receivingMethod", val)}
            />
          </Col>
          <Col md={true}>
            <CommonSelect
              label="Relationship to sender"
              options={[
                { value: "Business partner", label: "Business partner" },
                { value: "Customer", label: "Customer" },
                { value: "Employee", label: "Employee" },
                { value: "Vendor", label: "Vendor" },
                { value: "Others", label: "Others" },
              ]}
              value={formData.relationship}
              onChange={(val) => handleChange("relationship", val)}
            />
          </Col>
        </SectionContainer>

        {isBankTransfer && isIndividual && (
          <>
            <SectionContainer title="ID information">
              <Col md={3}>
                <CommonSelect
                  label="ID type"
                  placeholder="Select an option"
                  options={[{ value: "NRIC", label: "NRIC" }]}
                  value={formData.idType}
                  onChange={(val) => handleChange("idType", val)}
                />
              </Col>
              <Col md={3}>
                <CommonInput
                  label="ID number"
                  placeholder="Enter ID number"
                  value={formData.idNumber}
                  onChange={(e) => handleChange("idNumber", e.target.value)}
                />
              </Col>
              <Col md={3}>
                <CommonSelect
                  label="ID Expiry Date"
                  placeholder="Select ID expiry date"
                  options={[]}
                  value={formData.idExpiryDate}
                  onChange={(val) => handleChange("idExpiryDate", val)}
                />
              </Col>
              <Col md={3}>
                <CommonSelect
                  label="Date of birth"
                  placeholder="Select date of birth"
                  options={[]}
                  value={formData.dob}
                  onChange={(val) => handleChange("dob", val)}
                />
              </Col>
            </SectionContainer>

            <SectionContainer title="Personal information">
              <Col md={true} style={{ minWidth: "200px" }}>
                <CommonInput
                  label="First name"
                  placeholder="Enter first name"
                  value={formData.firstName}
                  onChange={(e) => handleChange("firstName", e.target.value)}
                />
              </Col>
              <Col md={true} style={{ minWidth: "200px" }}>
                <CommonInput
                  label="Middle name"
                  placeholder="Enter middle name"
                  value={formData.middleName}
                  onChange={(e) => handleChange("middleName", e.target.value)}
                />
              </Col>
              <Col md={true} style={{ minWidth: "200px" }}>
                <CommonInput
                  label="Last name"
                  placeholder="Enter last name"
                  value={formData.lastName}
                  onChange={(e) => handleChange("lastName", e.target.value)}
                />
              </Col>
              <Col md={true} style={{ minWidth: "200px" }}>
                <CommonInput
                  label="Middle initial"
                  placeholder="Enter middle initial"
                  value={formData.middleInitial}
                  onChange={(e) =>
                    handleChange("middleInitial", e.target.value)
                  }
                />
              </Col>
              <Col md={true} style={{ minWidth: "200px" }}>
                <CommonInput
                  label="Name of joint account holder"
                  placeholder="Enter name of joint account holder"
                  value={formData.jointAccountHolder}
                  onChange={(e) =>
                    handleChange("jointAccountHolder", e.target.value)
                  }
                />
              </Col>
              <Col md={4}>
                <CommonInput
                  label="Tax identification number"
                  placeholder="Enter tax identification number"
                  value={formData.taxId}
                  onChange={(e) => handleChange("taxId", e.target.value)}
                />
              </Col>
              <Col md={4}>
                <CommonInput
                  label="Company registration number"
                  placeholder="Enter company registration number"
                  value={formData.companyReg}
                  onChange={(e) => handleChange("companyReg", e.target.value)}
                />
              </Col>
              <Col md={4}>
                <CommonInput
                  label="Native name"
                  placeholder="Enter native name"
                  value={formData.nativeName}
                  onChange={(e) => handleChange("nativeName", e.target.value)}
                />
              </Col>
            </SectionContainer>

            <SectionContainer title="Contact Information">
              <Col md={4}>
                <label className="text-black fw-bold mb-1 fs-14">
                  Contact number
                </label>
                <div
                  className="d-flex border rounded-2 bg-white px-2 align-items-center"
                  style={{ height: "45px", borderColor: "#d5dfe7" }}
                >
                  <span className="text-muted pe-2 border-end me-2 fs-14">
                    +65
                  </span>
                  <input
                    type="text"
                    className="border-0 w-100 shadow-none outline-none fs-14"
                    placeholder="Enter contact number"
                    style={{ outline: "none" }}
                    value={formData.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                  />
                </div>
              </Col>
            </SectionContainer>

            <SectionContainer title="Address information">
              <Col md={true} style={{ minWidth: "180px" }}>
                <CommonInput
                  label="Address"
                  placeholder="Enter address"
                  value={formData.address}
                  onChange={(e) => handleChange("address", e.target.value)}
                />
              </Col>
              <Col md={true} style={{ minWidth: "180px" }}>
                <CommonInput
                  label="City"
                  placeholder="Enter city"
                  value={formData.city}
                  onChange={(e) => handleChange("city", e.target.value)}
                />
              </Col>
              <Col md={true} style={{ minWidth: "180px" }}>
                <CommonInput
                  label="State"
                  placeholder="Enter state"
                  value={formData.state}
                  onChange={(e) => handleChange("state", e.target.value)}
                />
              </Col>
              <Col md={true} style={{ minWidth: "180px" }}>
                <CommonInput
                  label="Postal code"
                  placeholder="Enter postal code"
                  value={formData.postalCode}
                  onChange={(e) => handleChange("postalCode", e.target.value)}
                />
              </Col>
              <Col md={true} style={{ minWidth: "180px" }}>
                <CommonInput
                  label="Suburb"
                  placeholder="Enter suburb"
                  value={formData.suburb}
                  onChange={(e) => handleChange("suburb", e.target.value)}
                />
              </Col>
            </SectionContainer>

            <SectionContainer title="Account details">
              <Col md={true} style={{ minWidth: "200px" }}>
                <CommonSelect
                  label="Bank"
                  placeholder="Select an option"
                  options={[]}
                  value={formData.bank}
                  onChange={(val) => handleChange("bank", val)}
                />
              </Col>
              <Col md={true} style={{ minWidth: "200px" }}>
                <CommonInput
                  label="IBAN number"
                  placeholder="Enter IBAN number"
                  value={formData.iban}
                  onChange={(e) => handleChange("iban", e.target.value)}
                />
              </Col>
              <Col md={true} style={{ minWidth: "200px" }}>
                <CommonInput
                  label="Account number"
                  placeholder="Enter account number"
                  value={formData.accountNumber}
                  onChange={(e) =>
                    handleChange("accountNumber", e.target.value)
                  }
                />
              </Col>
              <Col md={true} style={{ minWidth: "200px" }}>
                <CommonSelect
                  label="Account type"
                  placeholder="Select an option"
                  options={[]}
                  value={formData.accountType}
                  onChange={(val) => handleChange("accountType", val)}
                />
              </Col>
              <Col md={true} style={{ minWidth: "200px" }}>
                <CommonInput
                  label="Bank account title"
                  placeholder="Enter bank account title"
                  value={formData.bankAccountTitle}
                  onChange={(e) =>
                    handleChange("bankAccountTitle", e.target.value)
                  }
                />
              </Col>

              <Col md={true} style={{ minWidth: "200px" }}>
                <CommonInput
                  label="Transit number"
                  placeholder="Enter transit number"
                  value={formData.transitNumber}
                  onChange={(e) =>
                    handleChange("transitNumber", e.target.value)
                  }
                />
              </Col>
              <Col md={true} style={{ minWidth: "200px" }}>
                <CommonInput
                  label="Sort code"
                  placeholder="Enter sort code"
                  value={formData.sortCode}
                  onChange={(e) => handleChange("sortCode", e.target.value)}
                />
              </Col>
              <Col md={true} style={{ minWidth: "200px" }}>
                <CommonInput
                  label="Routing number"
                  placeholder="Enter routing number"
                  value={formData.routingNumber}
                  onChange={(e) =>
                    handleChange("routingNumber", e.target.value)
                  }
                />
              </Col>
              <Col md={true} style={{ minWidth: "200px" }}>
                <CommonInput
                  label="BSB number"
                  placeholder="Enter BSB number"
                  value={formData.bsbNumber}
                  onChange={(e) => handleChange("bsbNumber", e.target.value)}
                />
              </Col>
              <Col md={true} style={{ minWidth: "200px" }}>
                <CommonInput
                  label="Branch name"
                  placeholder="Enter branch name"
                  value={formData.branchName}
                  onChange={(e) => handleChange("branchName", e.target.value)}
                />
              </Col>

              <Col md={true} style={{ minWidth: "200px" }}>
                <CommonInput
                  label="Branch code"
                  placeholder="Enter branch code"
                  value={formData.branchCode}
                  onChange={(e) => handleChange("branchCode", e.target.value)}
                />
              </Col>
              <Col md={true} style={{ minWidth: "200px" }}>
                <CommonInput
                  label="Branch number"
                  placeholder="Enter branch number"
                  value={formData.branchNumber}
                  onChange={(e) => handleChange("branchNumber", e.target.value)}
                />
              </Col>
              <Col md={true} style={{ minWidth: "200px" }}>
                <CommonInput
                  label="IFSC number"
                  placeholder="Enter IFSC number"
                  value={formData.ifscNumber}
                  onChange={(e) => handleChange("ifscNumber", e.target.value)}
                />
              </Col>
              <Col md={true} style={{ minWidth: "200px" }}>
                <CommonInput
                  label="SWIFT code"
                  placeholder="Enter SWIFT code"
                  value={formData.swiftCode}
                  onChange={(e) => handleChange("swiftCode", e.target.value)}
                />
              </Col>
              <Col md={true} style={{ minWidth: "200px" }}>
                <CommonInput
                  label="Institution number"
                  placeholder="Enter institution number"
                  value={formData.institutionNumber}
                  onChange={(e) =>
                    handleChange("institutionNumber", e.target.value)
                  }
                />
              </Col>
            </SectionContainer>
          </>
        )}
      </div>
    </CommonModal>
  );
};

export default CreateBeneficiaryModal;
